import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Send, PenLine, ShieldCheck, Mail, MessageSquare, FolderOpen, Webhook } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { formatDateTime } from "@/lib/format";

const ICONS = { email: Mail, msteams: MessageSquare, sharepoint: FolderOpen, other: Webhook };

/**
 * Reusable "Close out & submit" dialog used by Meetings and Audits/Checklists.
 *
 * Props:
 *   open, onClose
 *   kind: "meeting" | "audit"
 *   title: human title of the form being closed
 *   endpoint: API path that accepts {destination_ids, note, signature_name}
 *   onClosed: callback after successful close-out
 */
export default function CloseOutDialog({ open, onClose, kind, title, endpoint, onClosed }) {
  const { user } = useAuth();
  const [destinations, setDestinations] = useState([]);
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState("");
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNote(""); setSelected([]); setSig(user?.name || "");
    api.get("/destinations").then((r) => setDestinations((r.data || []).filter((d) => d.active !== false))).catch(() => setDestinations([]));
  }, [open, user]);

  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(endpoint, {
        destination_ids: selected,
        note,
      });
      const detail = e => e?.response?.data?.detail;
      const sentCount = (r.data?.dispatches || []).length;
      toast.success(`${kind === "meeting" ? "Meeting" : "Audit"} closed & signed${sentCount ? ` · sent to ${sentCount} destination${sentCount === 1 ? "" : "s"}` : ""}`);
      onClosed?.(r.data);
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d :
                  d?.message ? `${d.message} · ${(d.missing || []).map((m) => `Item ${m.index + 1}`).join(", ")}` :
                  "Close-out failed";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const noun = kind === "meeting" ? "meeting & submit minutes" : "audit & submit report";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-2xl border-[#1a1a1a]/15 max-h-[90vh] overflow-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-xl flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#ff4500]" /> Close out {noun}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-[#fff1ea] border border-[#ff4500]/20 rounded-xl p-3">
            <div className="font-display font-bold text-sm">{title}</div>
            <div className="text-[11px] text-[#1a1a1a]/75 mt-1">This locks the record, generates a tamper-evident signed PDF, and (optionally) sends the PDF to the destinations you select below.</div>
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff4500] mb-2">// Send signed PDF to</div>
            {destinations.length === 0 ? (
              <div className="bg-[#f7f5f2] rounded-xl p-3 text-[12px] text-[#1a1a1a]/65">
                No destinations configured yet. Closing out will still generate the signed PDF; an Admin can set up email / MS Teams / SharePoint destinations on the <b>Admin · Destinations</b> page so future close-outs route automatically.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="closeout-destinations">
                {destinations.map((d) => {
                  const Icon = ICONS[d.type] || Send;
                  const on = selected.includes(d.id);
                  return (
                    <button key={d.id} type="button" data-testid={`closeout-dest-${d.id}`} onClick={() => toggle(d.id)}
                      className={`text-left px-3 py-2.5 rounded-xl border flex items-center gap-3 transition-all ${on ? "bg-[#1a1a1a] text-white border-[#1a1a1a]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/15 hover:border-[#ff4500]"}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${on ? "bg-[#ff4500] text-white" : "bg-[#fff1ea] text-[#ff4500]"}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold text-sm truncate">{d.name}</div>
                        <div className={`text-[10px] uppercase tracking-wider font-mono truncate ${on ? "text-white/65" : "text-[#1a1a1a]/55"}`}>{d.type}{d.type !== "email" ? " · MOCKED" : ""}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Close-out note (optional)</label>
            <textarea data-testid="closeout-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={kind === "meeting" ? "Headline outcome, decisions, next steps…" : "Summary of this audit (passes, fails, follow-ups)…"}
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm h-20" />
          </div>

          <div className="bg-[#1a1a1a] text-white rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <PenLine className="w-3.5 h-3.5 text-[#ff5f15]" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff5f15]">// Signed automatically from your profile</div>
                <div className="font-display font-bold text-sm">{user?.name || "—"}{user?.employee_id ? ` · ${user.employee_id}` : ""}</div>
              </div>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-white/55">
              {formatDateTime(new Date())} · Signature is locked to your profile — edit it under <span className="text-[#ff5f15]">Profile → Edit</span> if incorrect.
            </div>
          </div>
        </div>

        <DialogFooter>
          <button data-testid="closeout-submit" onClick={submit} disabled={busy}
            className="btn-pill btn-pill-primary disabled:opacity-50">
            <ShieldCheck className="w-3.5 h-3.5" /> {busy ? "Closing…" : `Close out ${kind} & submit`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
